/**
 * coachJourney.ts — the guided tour's persistence + eligibility rules.
 *
 * `CoachUserLike` is structural on purpose: it only names the fields these
 * functions read, so tests can pass plain object literals instead of a real
 * AuthContext `User`, and so this module never has to import AuthContext
 * (which would drag React along into a pure-logic file).
 *
 * Read path: server (`user.tours` / `user.journey`, from `/auth/me`) wins
 * whenever present; the v2 localStorage cache (utils/coachMarks.ts) is the
 * fallback for a write that hasn't round-tripped yet; v1's `fd:coach-marks`
 * is the last resort for someone who used the tour before this rewrite.
 *
 * Write path never throws — a coaching feature must never be the thing that
 * breaks the app. Every write updates the cache synchronously (so a second
 * read in the same tick, or a second tab, sees it immediately) and then
 * fires the PUT; failures are swallowed and left for `syncCoachState` to
 * retry, except a 4xx, which means the server will never accept that exact
 * request and retrying it forever would be pointless.
 */

import type { MilestoneName } from './coachEvents';
import type { CoachNudge, CoachNudgeAudience } from './coachTypes';
import { matchRoutePattern } from './coachRoutes';
import {
  type CachedTourRecord,
  type TourRecordDTO,
  type TourStatus,
  readUserCache,
  writeCachedTour,
  markTourSynced,
  deleteCachedTour,
  writeCachedMilestone,
  markMilestoneSynced,
  readLegacyTours,
  deleteLegacyTour,
  legacyToRecord,
} from '../utils/coachMarks';

export interface JourneyDTO {
  startedAt?: string | null;
  completedAt?: string | null;
  milestones?: {
    firstApp?: string | null;
    firstBuild?: string | null;
    firstPublish?: string | null;
  } | null;
}

export interface CoachUserLike {
  id: string;
  allowed?: boolean;
  hasPublished?: boolean;
  tours?: Record<string, TourRecordDTO>;
  journey?: JourneyDTO | null;
}

export const DESKTOP_MIN_WIDTH = 768;
export const NUDGE_KEY_PREFIX = 'nudge-';

function isNudgeKey(key: string): boolean {
  return key.startsWith(NUDGE_KEY_PREFIX);
}

/** server record → v2 cache → legacy (promoted shape, via `legacyToRecord`). */
export function getTourRecord(user: CoachUserLike | null | undefined, tourId: string): TourRecordDTO | null {
  const serverRecord = user?.tours?.[tourId];
  if (serverRecord) return serverRecord;
  const userId = user?.id;
  if (!userId) return null;
  const cached = readUserCache(userId).tours[tourId];
  // The server is the truth and `/me` always sends `tours` (`{}` when empty).
  // A cached record that already synced but is missing from that map is
  // stale — it was removed server-side (reset-onboarding, a staff clear) —
  // so only a pending, unsynced write may still stand in for the server.
  if (cached && (!cached.synced || !user?.tours)) return cached;
  const legacy = readLegacyTours(userId)[tourId];
  if (legacy) return legacyToRecord(legacy);
  return null;
}

/** Any tour (not nudge) record anywhere — server, cache, or legacy. */
export function hasAnyTourRecord(user: CoachUserLike | null | undefined): boolean {
  if (!user) return false;
  const serverKeys = Object.keys(user.tours || {}).filter((k) => !isNudgeKey(k));
  if (serverKeys.length > 0) return true;
  if (!user.id) return false;
  const cacheKeys = Object.entries(readUserCache(user.id).tours)
    .filter(([k, rec]) => !isNudgeKey(k) && (!rec.synced || !user.tours))
    .map(([k]) => k);
  if (cacheKeys.length > 0) return true;
  const legacyKeys = Object.keys(readLegacyTours(user.id)).filter((k) => !isNudgeKey(k));
  return legacyKeys.length > 0;
}

export function isJourneyUser(user: CoachUserLike | null | undefined): boolean {
  return !!user?.journey?.startedAt;
}

/** Desktop-width, and not on a surface the tour must never appear on
 *  (the waitlist wall, onboarding itself, or an auth redirect page). */
export function isCoachSurfaceOk(viewportWidth: number, pathname: string): boolean {
  if (viewportWidth < DESKTOP_MIN_WIDTH) return false;
  return !/^\/(access|waitlist|onboarding|auth)(\/|$)/.test(pathname);
}

/**
 * Deliberately NOT gated on `journey.completedAt`: after the aha moment, a
 * journey user still gets each `auto: new-user` page tour once on first
 * visit — that's the "guide them as they use the app" half of the feature.
 * `autoToursSuppressed` is the brake on that, not this.
 */
export function isAutoShowEligible(user: CoachUserLike | null | undefined, viewportWidth: number, pathname: string): boolean {
  if (!isJourneyUser(user)) return false;
  if (user?.allowed === false) return false;
  return isCoachSurfaceOk(viewportWidth, pathname);
}

const MILESTONE_JOURNEY_FIELD: Partial<Record<MilestoneName, 'firstApp' | 'firstBuild' | 'firstPublish'>> = {
  'first-app': 'firstApp',
  'first-build': 'firstBuild',
  'first-publish': 'firstPublish',
};

function hasServerMilestone(user: CoachUserLike | null | undefined, name: MilestoneName): boolean {
  if (!user) return false;
  if (name === 'journey-done') return !!user.journey?.completedAt;
  const field = MILESTONE_JOURNEY_FIELD[name];
  if (field && user.journey?.milestones?.[field]) return true;
  if (name === 'first-publish' && user.hasPublished === true) return true;
  return false;
}

/** Server journey field (or `hasPublished` for `first-publish`) wins; the
 *  cache is the fallback for a milestone PUT that hasn't round-tripped yet.
 *  Never infers `first-build` from `tasksSucceededCount` or anything else
 *  — that field can count plan turns, not just builds. */
export function hasMilestone(user: CoachUserLike | null | undefined, name: MilestoneName): boolean {
  if (hasServerMilestone(user, name)) return true;
  if (!user?.id) return false;
  return !!readUserCache(user.id).milestones[name];
}

function collectTourIds(user: CoachUserLike | null | undefined): string[] {
  const ids = new Set<string>();
  Object.keys(user?.tours || {}).forEach((k) => ids.add(k));
  if (user?.id) {
    Object.keys(readUserCache(user.id).tours).forEach((k) => ids.add(k));
    Object.keys(readLegacyTours(user.id)).forEach((k) => ids.add(k));
  }
  return Array.from(ids).filter((k) => !isNudgeKey(k));
}

/** ≥2 non-nudge tour records dismissed and none completed → stop offering
 *  `full` auto tours (manual starts and `update` runs still work). The
 *  brake for someone who keeps closing every tour that pops up. */
export function autoToursSuppressed(user: CoachUserLike | null | undefined): boolean {
  let dismissed = 0;
  let completed = 0;
  for (const id of collectTourIds(user)) {
    const record = getTourRecord(user, id);
    if (!record) continue;
    if (record.status === 'completed') completed += 1;
    else if (record.status === 'dismissed') dismissed += 1;
  }
  return dismissed >= 2 && completed === 0;
}

export function routeMatches(routes: string[], pathname: string): boolean {
  return routes.some((pattern) => matchRoutePattern(pattern, pathname));
}

function audienceOk(user: CoachUserLike | null | undefined, audience: CoachNudgeAudience): boolean {
  if (audience === 'journey') return isJourneyUser(user);
  if (audience === 'existing') return !isJourneyUser(user) && !hasAnyTourRecord(user);
  return true; // 'all'
}

export function nudgeDecision(
  user: CoachUserLike | null | undefined,
  nudge: CoachNudge,
  fired: { name: string },
  ctx: { viewportWidth: number; pathname: string },
): boolean {
  if (nudge.when !== fired.name) return false;
  if (!isCoachSurfaceOk(ctx.viewportWidth, ctx.pathname)) return false;
  if (!routeMatches(nudge.routes, ctx.pathname)) return false;
  if (nudge.once && getTourRecord(user, NUDGE_KEY_PREFIX + nudge.id)) return false;
  if (nudge.after && !hasMilestone(user, nudge.after as MilestoneName)) return false;
  if (nudge.unless && hasMilestone(user, nudge.unless as MilestoneName)) return false;
  if (nudge.resumes && getTourRecord(user, nudge.resumes)?.status !== 'dismissed') return false;
  return audienceOk(user, nudge.audience);
}

export interface CoachWriteDeps {
  setTourState: (body: {
    tour: string;
    status: TourStatus;
    version: number;
    lastStepIndex?: number;
  }) => Promise<{ data: { tours: Record<string, TourRecordDTO> } }>;
  recordJourneyMilestone: (m: MilestoneName) => Promise<{ data: { journey: JourneyDTO | null } }>;
  mergeUser: (patch: Partial<CoachUserLike>) => void;
}

function isClientError(err: unknown): boolean {
  const status = (err as { response?: { status?: number } } | undefined)?.response?.status;
  return typeof status === 'number' && status >= 400 && status < 500;
}

/** Persists how a run of `tourId` (or `nudge-<id>`) ended. Cache write is
 *  synchronous, before the PUT even starts, so a reload a moment later
 *  still sees it. Never throws. */
export async function recordTourEnd(
  user: CoachUserLike | null | undefined,
  tourId: string,
  end: { status: TourStatus; version: number; lastStepIndex?: number },
  deps: CoachWriteDeps,
): Promise<void> {
  const userId = user?.id;
  if (!userId) return;

  const cached: CachedTourRecord = {
    status: end.status,
    version: end.version,
    lastStepIndex: end.lastStepIndex,
    completedAt: new Date().toISOString(),
    synced: false,
  };
  writeCachedTour(userId, tourId, cached);

  try {
    const res = await deps.setTourState({
      tour: tourId,
      status: end.status,
      version: end.version,
      lastStepIndex: end.lastStepIndex,
    });
    markTourSynced(userId, tourId);
    deps.mergeUser({ tours: res.data.tours });
  } catch (err) {
    if (isClientError(err)) {
      // The server will never accept this exact request — retrying it on
      // every future syncCoachState would just repeat the same 4xx forever.
      markTourSynced(userId, tourId);
    }
    // 5xx / network: leave unsynced — syncCoachState retries later.
  }
}

export function recordNudgeShown(user: CoachUserLike | null | undefined, nudgeId: string, deps: CoachWriteDeps): Promise<void> {
  return recordTourEnd(user, NUDGE_KEY_PREFIX + nudgeId, { status: 'completed', version: 1 }, deps);
}

/** The once-gate for a milestone: `false` (and no PUT at all) if the user
 *  already has it. Otherwise writes the cache SYNCHRONOUSLY (before any
 *  await) so a second tab racing the same event dedupes against it, then
 *  fires the PUT. Returns `true` whenever this call is the one that newly
 *  recorded it, regardless of whether the PUT itself lands. Never throws. */
export async function recordMilestone(
  user: CoachUserLike | null | undefined,
  name: MilestoneName,
  deps: CoachWriteDeps,
): Promise<boolean> {
  const userId = user?.id;
  if (!userId) return false;
  if (hasMilestone(user, name)) return false;

  writeCachedMilestone(userId, name, false);

  try {
    const res = await deps.recordJourneyMilestone(name);
    markMilestoneSynced(userId, name);
    deps.mergeUser({ journey: res.data.journey });
  } catch {
    // 5xx / network — leave unsynced for syncCoachState to retry. A
    // milestone name is a fixed, compiler-checked enum, so there is no
    // client-shaped 4xx to give up retrying on the way recordTourEnd has.
  }
  return true;
}

// Once per session per userId — re-checking the legacy key and re-flushing
// the cache on every render would be wasted work for something that only
// ever changes as a side effect of this same module's writes.
const syncedSessions = new Set<string>();

/** Test-only escape hatch for the once-per-session guard above. */
export function __resetForTests(): void {
  syncedSessions.clear();
}

/**
 * Runs once per session per user:
 *   1. Promotes any v1 `fd:coach-marks` tour the server hasn't seen yet
 *      (PUT it, then delete the legacy entry); a tour the server already
 *      has is just deleted locally — the server already won that race.
 *   2. Flushes any v2 cache record still marked unsynced (a previous PUT
 *      that never landed), with the same "server already has it" check.
 * Server always wins on conflict; every failure is left in place as its
 * own retry signal for next time.
 */
export async function syncCoachState(user: CoachUserLike | null | undefined, deps: CoachWriteDeps): Promise<void> {
  const userId = user?.id;
  if (!userId) return;
  if (syncedSessions.has(userId)) return;
  syncedSessions.add(userId);

  const legacy = readLegacyTours(userId);
  for (const [tourId, legacyRecord] of Object.entries(legacy)) {
    if (user?.tours?.[tourId]) {
      deleteLegacyTour(userId, tourId);
      continue;
    }
    const record = legacyToRecord(legacyRecord);
    try {
      const res = await deps.setTourState({
        tour: tourId,
        status: record.status,
        version: record.version,
        lastStepIndex: record.lastStepIndex,
      });
      deleteLegacyTour(userId, tourId);
      deps.mergeUser({ tours: res.data.tours });
    } catch {
      /* leave it — presence is the retry signal for next session */
    }
  }

  const cache = readUserCache(userId);
  for (const [tourId, rec] of Object.entries(cache.tours)) {
    if (rec.synced) {
      // Already landed once; if the server no longer has it, the record was
      // removed server-side and this copy is stale — prune it.
      if (user?.tours && !user.tours[tourId]) deleteCachedTour(userId, tourId);
      continue;
    }
    if (user?.tours?.[tourId]) {
      markTourSynced(userId, tourId);
      continue;
    }
    try {
      const res = await deps.setTourState({
        tour: tourId,
        status: rec.status,
        version: rec.version,
        lastStepIndex: rec.lastStepIndex,
      });
      markTourSynced(userId, tourId);
      deps.mergeUser({ tours: res.data.tours });
    } catch (err) {
      if (isClientError(err)) markTourSynced(userId, tourId);
    }
  }

  for (const [name, rec] of Object.entries(cache.milestones)) {
    if (rec.synced) continue;
    // Server-only check: hasMilestone would also read this very cache entry
    // and report a false "already has it".
    if (hasServerMilestone(user, name as MilestoneName)) {
      markMilestoneSynced(userId, name);
      continue;
    }
    try {
      const res = await deps.recordJourneyMilestone(name as MilestoneName);
      markMilestoneSynced(userId, name);
      deps.mergeUser({ journey: res.data.journey });
    } catch {
      /* leave unsynced */
    }
  }
}
